import {
  Button,
  Col,
  ColorPicker,
  DatePicker,
  Flex,
  Form,
  GetProp,
  Image,
  Input,
  message,
  Radio,
  Row,
  Select,
  Switch,
  Tag,
  Typography,
  Upload,
  UploadFile,
  UploadProps,
  Tooltip,
  Tabs,
} from "antd";
import type { FormProps } from "antd";
import Paragraph from "antd/es/typography/Paragraph";
import { useTranslation, Trans } from "react-i18next";
import { codeOptions } from "../../../../../constants/codeOptions";
import { nineOneAppMemberLevelOptions as memberLevelOptions } from "../../../../../constants/memberLevelOptions";
import { ecType } from "./ec.types";
import React, {
  useCallback,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  Ref,
} from "react";
import uploadImg from "../../../../../assets/icons/upload-img.svg";
import arrowDown from "../../../../../assets/icons/arrow-down.svg";
import tooltips from "../../../../../assets/icons/tooltips.svg";
import copyIcon from "../../../../../assets/icons/copy.svg";
import circleInfoIcon from "../../../../../assets/icons/circle-info.svg";
import link from "../../../../../assets/icons/link.svg";

const { Text } = Typography;

// Import credits name related constants and components
import { getCreditsTranslation, LANGUAGE_CONFIG, LanguageKey } from "../constants/creditsName";
import CreditsNameInput from "./CreditsNameInput";


type ECFormProps = {
  formValues: ecType;
  originalFormValues: ecType | null;
  platform: string;
  saveData: () => void;
  setFormValues: React.Dispatch<React.SetStateAction<ecType | null>>;
};

type ECFormHandle = {
  handleManualSubmit: () => void;
};

type ColorPickerFormItemProps = {
  value?: string;
  onChange?: (value: string) => void;
};

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

const ColorPickerFormItem: React.FC<ColorPickerFormItemProps> = ({
  value,
  onChange,
}: ColorPickerFormItemProps) => {
  return (
    <ColorPicker
      defaultValue={value}
      onChangeComplete={(e) => onChange && onChange(`#${e.toHex()}`)}
      disabledAlpha={true}
    >
      <Button
        style={{ background: value }}
        className="w-full outline-none border-lineColor"
      />
    </ColorPicker>
  );
};

const ECForm = forwardRef<ECFormHandle, ECFormProps>(
  (props: ECFormProps, ref: Ref<ECFormHandle>) => {
    const {
      formValues,
      originalFormValues,
      platform,
      setFormValues,
      saveData,
    } = props;
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [fileError, setFileError] = useState<string | null>(null);
    const [formTouched, setFormTouched] = useState<boolean>(false);
    const [previewImage, setPreviewImage] = useState("");
    const [activeTab, setActiveTab] = useState("card");

    // Reset credits name to default value - use useCallback to avoid recreating
    const handleResetCreditsName = useCallback((language: LanguageKey) => {
      const newValue = getCreditsTranslation()[language];
      
      // Set form field value
      form.setFieldValue(["custom_credits_name_mapping", language], newValue);
      
      // Manually trigger validation to clear any error messages
      form.validateFields([["custom_credits_name_mapping", language]]);
      
      // Trigger form's onValuesChange to maintain consistency
      const currentValues = form.getFieldsValue();
      const newMapping = {
        ...currentValues.custom_credits_name_mapping,
        [language]: newValue
      };
      
      // Simulate onValuesChange call
      setFormValues((prev) => {
        if (!prev) return prev;
        const mergedValues = { ...prev, custom_credits_name_mapping: newMapping };
        return mergedValues;
      });
    }, [form, setFormValues]);


    // TO OPTIMIZE: consider using api to get the disabledQRCode instead of hardcoding it
    const disabledQRCode = platform === "flaps";
    const barcodeOptions = disabledQRCode
      ? codeOptions.filter((option) => option.value !== "QRCODE")
      : codeOptions;

    const barcodeValueSourceOptions = [
      { value: "customer_id", label: t("Customer ID") },
      { value: "email", label: t("Email") },
      { value: "phone", label: t("Phone") },
      { value: "local_phone", label: t("Local Phone") },
      ...(platform === "91app"
        ? [
          { value: "ec_customer_id", label: "EC Customer ID" },
          { value: "custom_outer_code_1", label: "Custom Outer Code 1" },
        ]
        : []),
      ...(platform === "flaps"
        ? [{ value: "member_card_no", label: t("Member Card No") }]
        : []),
    ];

    const barcodeDescriptions = {
      CODE39: t("barcode_description_CODE39"),
      CODE128: t("barcode_description_CODE128"),
      EAN13: t("barcode_description_EAN13"),
      QRCODE: t("barcode_description_QRCODE"),
    };

    const handleManualSubmit = () => {
      form.submit();
    };

    useImperativeHandle(ref, () => ({
      handleManualSubmit: handleManualSubmit,
    }));

    const onFileChange = ({ fileList }: { fileList: UploadFile[] }) => {
      if (fileList.length > 0) {
        setFormTouched(false);
      } else {
        setFormTouched(true);
      }

      const logoFile = fileList.slice(-1);

      setFormValues({
        ...formValues,
        logo_url: URL.createObjectURL(
          logoFile[0]?.originFileObj as unknown as File
        ),
        logo: logoFile,
      }); // Keep only the latest file
      form.setFieldsValue({ logo: fileList.slice(-1) });
      handlePreview(fileList.slice(-1)[0]);
    };

    const handlePreview = async (file: UploadFile) => {
      type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];
      const getBase64 = (file: FileType): Promise<string> =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = (error) => reject(error);
        });
      if (!file.url && !file.preview) {
        file.preview = await getBase64(file.originFileObj as FileType);
      }

      setPreviewImage(file.url || (file.preview as string));
    };

    const beforeUpload = (file: FileType) => {
      const isSvgOrPng =
        file.type === "image/svg+xml" || file.type === "image/png";
      if (!isSvgOrPng) {
        message.error("You can only upload SVG/PNG file!");
        setFileError("You can only upload SVG/PNG file!");
        setPreviewImage("");
      }
      const isLt2M = file.size / 1024 / 1024 <= 1;
      if (!isLt2M) {
        message.error("Image must smaller than 1MB!");
        setFileError("Image must smaller than 1MB!");
        setPreviewImage("");
      }
      if (isSvgOrPng && isLt2M) {
        setFileError(null);
      }
      return isSvgOrPng && isLt2M;
    };

    const validateFile = useCallback(() => {
      if (formValues.logo.length === 0) {
        setFileError("Please upload a file!");
        return false;
      }
      return true;
    }, [formValues.logo.length]);

    const isValidURL = (string: string) => {
      const urlPattern =
        /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:[0-9]+)?(\/[^\s]*)?$/i;
      return urlPattern.test(string);
    };

    const resetLogoFile = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const logoUrl = originalFormValues?.logo_url
        ? originalFormValues.logo_url
        : "";
      setFormValues({
        ...formValues,
        logo_url: logoUrl,
        logo: [],
      });
    };

    useEffect(() => {
      if (formTouched) {
        validateFile();
        form.validateFields(["logo"]);
      }
    }, [form, formTouched, formValues.logo, validateFile]);

    useEffect(() => {
      // Initialize credits name default values - only fill default when no value exists
      const creditsTranslations = getCreditsTranslation();
      const formData = {
        ...formValues,
        custom_credits_name_mapping: Object.fromEntries(
          Object.entries(creditsTranslations).map(([lang, defaultValue]) => [
            lang, 
            formValues.custom_credits_name_mapping?.[lang] ?? defaultValue
          ])
        )
      };
      
      form.setFieldsValue(formData);
      setPreviewImage(formValues.logo_url);
    }, [form, formValues]);

    useEffect(() => {
      form.validateFields([
        "show_member_level",
        "enable_member_level_name",
        "is_enabled_faq",
        "is_enabled_member_benefits",
        "is_enabled_barcode",
        "is_enabled_annual_consumption",
        "is_enabled_order",
        "is_enabled_show_order_after_date",
      ]);
    }, [
      formValues.show_member_level,
      formValues.enable_member_level_name,
      formValues.is_enabled_faq,
      formValues.is_enabled_member_benefits,
      formValues.is_enabled_barcode,
      formValues.is_enabled_annual_consumption,
      formValues.is_enabled_order,
      formValues.is_enabled_show_order_after_date,
      form,
    ]);

    const copyToClipboard = (text: string) => {
      navigator.clipboard.writeText(text);
      message.success(t("Copied to clipboard"));
    };

    const onFinished: FormProps["onFinish"] = () => {
      saveData();
    };

    const messagingFields = ["sms_brand_name"];

    const onFinishFailed: FormProps["onFinishFailed"] = ({ errorFields }) => {
      validateFile();
      // Hidden tabs stay mounted, so their errors would otherwise go unseen.
      if (errorFields.length > 0) {
        setActiveTab(messagingFields.includes(String(errorFields[0].name[0])) ? "messaging" : "card");
      }
    };

    const urlLink =
      "https://crescendolab.zendesk.com/hc/zh-tw/articles/40000702781849-%E6%95%99%E5%AD%B8-91APP-%E6%8E%A8%E8%96%A6%E4%BA%BA%E7%B6%81%E5%AE%9A%E4%B8%B2%E6%8E%A5";

    return (
      <Form
        initialValues={formValues}
        name="ec-module"
        layout="vertical"
        form={form}
        className="min-w-[300px] w-[95%] max-w-[400px]"
        onFinish={onFinished}
        onFinishFailed={onFinishFailed}
        onValuesChange={(values) => {
          setFormValues((prev) => {
            if (!prev) return prev;
            const mergedValues = { ...prev, ...values };

            Object.entries(values).forEach(([key, value]) => {
              if (value && typeof value === 'object' && !Array.isArray(value)) {
                // detect Record type, merge instead of overwrite
                const prevValue = (prev as Record<string, unknown>)[key];
                const mergedValue = prevValue && typeof prevValue === 'object' && !Array.isArray(prevValue)
                  ? { ...prevValue, ...value }
                  : value;
                (mergedValues as Record<string, unknown>)[key] = mergedValue;
              }
            });

            // If custom credits name is enabled, ensure all languages have values
            if (values.enable_custom_credits_name) {
              const creditsTranslations = getCreditsTranslation();
              mergedValues.custom_credits_name_mapping = {
                ...creditsTranslations,
                ...mergedValues.custom_credits_name_mapping,
              };
            }

            return mergedValues;
          });
        }}
      >
        <Flex vertical gap={"48px"}>
          <Flex vertical gap={"24px"}>
            <Paragraph className="h2-bold text-o-title">
              {t("Basic Information")}
            </Paragraph>
            <Flex vertical gap="8px">
              <Paragraph className="body-medium">
                {t("Integration status")}
              </Paragraph>
              <Tag
                style={{
                  padding: "2px 8px 2px 8px",
                  color: "#179C34",
                  backgroundColor: "#E7FBE7",
                  border: "none",
                }}
                className="w-fit rounded-[20px] note-medium"
              >
                {t("Connected")}
              </Tag>
              {originalFormValues?.show_referrer_info_box && (
                <div className="info-box body-regular">
                  <Flex gap="4px">
                    <div className="icon-container">
                      <Image src={circleInfoIcon} preview={false} />
                    </div>
                    <div>
                      <Trans
                        i18nKey="If you need to integrate 91app's referral binding, connect with in-store personnel through QR code scanning and use the enterprise-level CAAC AI chat platform to send shopping guide links and track both online and offline performance. Please <a>contact us now</a>."
                        components={{
                          a: (
                            <a
                              href={urlLink}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary"
                            ></a>
                          ),
                        }}
                      ></Trans>
                    </div>
                  </Flex>
                </div>
              )}
            </Flex>
            <Form.Item<ecType>
              label={t("Module Token")}
              name="module_token"
              layout="vertical"
              style={{ fontSize: "24px" }}
            >
              <Flex vertical gap="8px">
                <Input value={formValues.module_token} disabled />
                <Flex gap={"8px"} className="items-center">
                  <Image src={tooltips} preview={false} />
                  <Text className="module-token">
                    {t("The token will expire on")}{" "}
                    {formValues.module_token_expired_at.toLocaleDateString()}
                  </Text>
                </Flex>
              </Flex>
            </Form.Item>
          </Flex>
          <Flex vertical gap={"24px"}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              tabBarStyle={{ marginBottom: 0 }}
              items={[
                { key: "card", label: t("Member card settings") },
                { key: "messaging", label: t("Message push settings") },
              ]}
            />
            <Flex vertical gap={"48px"} style={activeTab === "card" ? undefined : { display: "none" }}>
              <Flex vertical gap={"24px"}>
                <Text className="h3-medium">{t("Brand settings")}</Text>
                {formValues.is_enabled_otp_methods_switch && (
                  <Form.Item<ecType>
                    label={t("Account Verification Settings")}
                    name="card_binding_source"
                    layout="vertical"
                    style={{ fontSize: "16px" }}
                  >
                    <Flex vertical gap={"4px"}>
                      <Radio.Group
                        value={formValues.card_binding_source}
                        style={{ display: "flex", flexDirection: "column", gap: 4 }}
                        options={[
                          { value: "phone", label: t("Phone OTP") },
                          { value: "email", label: t("Email OTP") },
                          {
                            value: "both",
                            label: t("Support both phone and Email OTP"),
                          },
                        ]}
                      />
                    </Flex>
                  </Form.Item>
                )}
                <Form.Item<ecType>
                  label={t("Member card URL")}
                  name="member_card_url"
                  layout="vertical"
                  style={{ fontSize: "16px" }}
                >
                  <Flex vertical gap={"4px"}>
                    <Flex
                      gap={"8px"}
                      align="center"
                      onClick={() => copyToClipboard(formValues.member_card_url)}
                    >
                      <Input
                        value={formValues.member_card_url}
                        readOnly
                        disabled
                        placeholder={t("Member card URL")}
                      />
                      <Image
                        src={copyIcon}
                        width={20}
                        preview={false}
                        className="cursor-pointer"
                      />
                    </Flex>
                    <Flex align="start" gap={"4px"} className="pt-0.5">
                      <Image src={link} width={18} preview={false} />
                      <Text type="warning" className="body-regular">
                        {t(
                          "Complete the following setup and save the settings to obtain the URL to the member card page."
                        )}
                      </Text>
                    </Flex>
                  </Flex>
                </Form.Item>
                <Flex vertical gap={"24px"}>
                  <Form.Item<ecType>
                    label={t("Page Title")}
                    layout="vertical"
                    style={{ fontSize: "24px" }}
                  >
                    <Text className="note-regular text-o-description">
                      {t(
                        "Your page title can have up to 50 characters, such as your brand name. It will be displayed in the browser title."
                      )}
                    </Text>
                    <Form.Item
                      name="brand_name"
                      style={{ marginTop: "10px" }}
                      rules={[
                        { max: 50, message: t("Must be within 50 characters.") },
                        { required: true, message: t("Page Title is required") },
                      ]}
                    >
                      <Input placeholder={t("Please enter Page Title")} />
                    </Form.Item>
                  </Form.Item>

                  {formValues.order_url && (
                    <Form.Item<ecType>
                      label={t("Load more orders URL")}
                      name="order_url"
                      style={{ fontSize: "24px" }}
                      rules={[
                        {
                          required: true,
                          message: t("Load more orders URL is required"),
                        },
                        {
                          validator: (_, value) =>
                            !value || isValidURL(value)
                              ? Promise.resolve()
                              : Promise.reject(
                                new Error(t("The input is not a valid URL!"))
                              ),
                        },
                      ]}
                    >
                      <Input
                        value={formValues.order_url}
                        placeholder={t("Please enter Load more orders URL")}
                      />
                    </Form.Item>
                  )}

                  <Form.Item<ecType>
                    label={t("LOGO")}
                    name="logo"
                    rules={[
                      {
                        required: !formValues.logo_url,
                        message: t("LOGO is required"),
                      },
                    ]}
                    layout="vertical"
                    style={{ fontSize: "24px" }}
                    valuePropName="fileList"
                    validateStatus={fileError ? "error" : ""}
                  >
                    <Upload.Dragger
                      style={{ border: fileError ? "1px solid #D93721" : "" }}
                      accept=".svg, .png"
                      fileList={formValues.logo}
                      showUploadList={false}
                      beforeUpload={beforeUpload}
                      onChange={onFileChange}
                      maxCount={1}
                      multiple={false}
                    >
                      <Flex vertical align="center" gap={"12px"}>
                        <Flex
                          className="w-[300px] h-[160px]"
                          align="center"
                          justify="center"
                        >
                          <Image
                            src={previewImage || uploadImg}
                            className="max-h-[160px]"
                            preview={false}
                          />
                        </Flex>
                        {previewImage ? (
                          <Flex
                            align="center"
                            className="pt-1 -mb-2 body-regular text-o-description"
                          >
                            <Button
                              type="link"
                              size="small"
                              className="text-sm"
                              onClick={resetLogoFile}
                              disabled={formValues.logo.length === 0}
                            >
                              {t("Cancel")}
                            </Button>
                            <Text className="text-sm">or</Text>
                            <Button type="link" size="small" className="text-sm">
                              {t("Upload another file")}
                            </Button>
                          </Flex>
                        ) : (
                          <Flex vertical gap={"4px"}>
                            <Flex gap={"4px"} justify="center">
                              <Paragraph className="body-medium text-o-title">
                                {t("Drag and drop or")}
                              </Paragraph>
                              <Paragraph className="body-medium text-primary">
                                {t("Choose file")}
                              </Paragraph>
                              <Paragraph className="body-medium text-o-title">
                                {t("to upload")}
                              </Paragraph>
                            </Flex>

                            <Paragraph className="body-regular text-o-description">
                              {t(`PNG (600*320px, 1MB max)`)}
                            </Paragraph>
                          </Flex>
                        )}
                      </Flex>
                    </Upload.Dragger>
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item<ecType>
                        label={t("Foreground color")}
                        name="foreground_color"
                        layout="vertical"
                      >
                        <ColorPickerFormItem />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item<ecType>
                        label={t("Background color")}
                        name="background_color"
                        layout="vertical"
                      >
                        <ColorPickerFormItem />
                      </Form.Item>
                    </Col>
                  </Row>
                </Flex>
              </Flex>
              <Flex vertical gap={"24px"}>
                <Flex vertical gap={"8px"}>
                  <Paragraph className="h2-medium text-o-title">
                    {t("Display settings")}
                  </Paragraph>
                  <Paragraph className="body-regular text-o-description">
                    {t("Customize the display items on the member card page.")}
                  </Paragraph>
                </Flex>
                <Flex vertical gap={"24px"}>
                  {/* Member Level Toggle - Only visible for non-Shopify platforms */}
                  {platform !== "shopify" && (
                    <Flex vertical gap={"8px"}>
                      <Flex gap="12px" className="items-center">
                        <Form.Item<ecType>
                          name="show_member_level"
                          layout="vertical"
                        >
                          <Switch size="small" />
                        </Form.Item>
                        <Paragraph className="body-medium text-o-body">
                          {t("membership_level")}
                        </Paragraph>
                      </Flex>

                      {/* Member Level Name Toggle - Only visible when show_member_level is enabled */}
                      {formValues.show_member_level && platform === "91app" && (
                        <Flex vertical gap={"8px"} className="pl-[48px]">
                          <Flex gap="12px" className="items-center">
                            <Form.Item<ecType>
                              name="enable_member_level_name"
                              layout="vertical"
                            >
                              <Switch size="small" />
                            </Form.Item>
                            <Paragraph className="body-medium text-o-body">
                              {t("enable_member_level_name")}
                            </Paragraph>
                          </Flex>

                          {/* Member Level Names Configuration */}
                          {formValues.enable_member_level_name && (
                            <Flex vertical gap="12px" className="pl-[48px]">
                              <Paragraph className="body-regular text-o-description">
                                {t("config_member_level_names")}
                              </Paragraph>
                              <Flex vertical gap="8px">
                                {memberLevelOptions.map((levelOption) => (
                                  <Flex key={levelOption.value} gap="12px" className="items-center">
                                    <Paragraph className="body-medium text-o-body w-[60px]">
                                      {levelOption.label}:
                                    </Paragraph>
                                    <Form.Item<ecType>
                                      name={["member_level_name_mapping", levelOption.value]}
                                      className="mb-0 flex-1"
                                      normalize={(value) => value?.trim()}
                                    >
                                      <Input
                                        placeholder={t("enter_level_name")}
                                        size="small"
                                      />
                                    </Form.Item>
                                  </Flex>
                                ))}
                              </Flex>
                            </Flex>
                          )}
                        </Flex>
                      )}
                    </Flex>
                  )}

                  {/* Barcode Toggle */}
                  <Flex vertical gap={"8px"}>
                    <Flex gap="12px" className="items-center">
                      <Form.Item<ecType>
                        name="is_enabled_barcode"
                        layout="vertical"
                      >
                        <Switch size="small" />
                      </Form.Item>
                      <Paragraph className="body-medium text-o-body">
                        {t("Barcode")}
                      </Paragraph>
                      <Tooltip
                        title={
                          <Flex vertical gap="8px">
                            <Text strong>{t("Barcode Type Explanations")}</Text>
                            {Object.entries(barcodeDescriptions).map(
                              ([type, desc]) => (
                                <Flex
                                  vertical
                                  key={type}
                                  className="tooltip-content"
                                >
                                  <Text strong>{type}</Text>
                                  <Text>
                                    {desc}
                                    {type === "CODE39" && (
                                      <Text strong>
                                        {" "}
                                        {t("barcode_description_CODE39_email")}
                                      </Text>
                                    )}
                                  </Text>
                                </Flex>
                              )
                            )}
                          </Flex>
                        }
                        color="white"
                        overlayStyle={{ maxWidth: "300px" }}
                      >
                        <Image
                          src={tooltips}
                          preview={false}
                          className="cursor-help"
                        />
                      </Tooltip>
                    </Flex>
                    {formValues.is_enabled_barcode && (
                      <>
                        <Form.Item<ecType>
                          name="barcode_type"
                          rules={[
                            {
                              required: formValues.is_enabled_barcode,
                              message: t("Code type is required"),
                            },
                          ]}
                          layout="vertical"
                          className="w-full pl-[48px]"
                        >
                          <Select
                            suffixIcon={
                              <Image src={arrowDown} preview={false} alt="arrow" />
                            }
                            placeholder={t("Select a code")}
                            disabled={!formValues.is_enabled_barcode}
                            options={[...barcodeOptions]}
                          />
                        </Form.Item>
                        <Form.Item<ecType>
                          name="barcode_value_source"
                          rules={[
                            {
                              required: formValues.is_enabled_barcode,
                              message: t("Barcode value source is required"),
                            },
                          ]}
                          layout="vertical"
                          className="w-full pl-[48px]"
                        >
                          <Select
                            suffixIcon={
                              <Image src={arrowDown} preview={false} alt="arrow" />
                            }
                            placeholder={t("Select barcode value source")}
                            disabled={!formValues.is_enabled_barcode}
                            options={barcodeValueSourceOptions}
                          />
                        </Form.Item>
                      </>
                    )}
                  </Flex>

                  {/* Annual Consumption Toggle */}
                  <Flex vertical gap={"8px"}>
                    <Flex gap="12px" className="items-center">
                      <Form.Item<ecType>
                        name="is_enabled_annual_consumption"
                        layout="vertical"
                      >
                        <Switch />
                      </Form.Item>
                      <Paragraph className="body-medium text-o-body">
                        {t("annual_consumption_toggle")}
                      </Paragraph>
                    </Flex>
                  </Flex>

                  {/* Custom Credits Name Toggle */}
                  {platform !== "shopify" && platform !== "91app" && (
                    <Flex vertical gap={"8px"}>
                      <Flex gap="12px" className="items-center">
                        <Form.Item<ecType>
                          name="enable_custom_credits_name"
                          layout="vertical"
                        >
                          <Switch size="small" />
                        </Form.Item>
                        <Paragraph className="body-medium text-o-body">
                          {t("enable_custom_credits_name")}
                        </Paragraph>
                      </Flex>

                      {/* Custom Credits Name Configuration - Only visible when toggle is enabled */}
                      {formValues.enable_custom_credits_name && (
                        <Flex vertical gap="12px" className="pl-[48px]">
                          {(() => {
                            const creditsTranslations = getCreditsTranslation();
                            return LANGUAGE_CONFIG.map((language) => (
                              <CreditsNameInput 
                                key={language}
                                language={language} 
                                label={t(`language_name_${language}`)} 
                                placeholder={`${t("default")}: ${creditsTranslations[language]}`}
                                enableCustomCreditsName={formValues.enable_custom_credits_name}
                                onReset={handleResetCreditsName}
                                t={t}
                              />
                            ));
                          })()}
                        </Flex>
                      )}
                    </Flex>
                  )}

                  {/* Member Benefits Toggle */}
                  <Flex vertical gap={"8px"}>
                    <Flex gap="12px" className="items-center">
                      <Form.Item<ecType>
                        name="is_enabled_member_benefits"
                        layout="vertical"
                      >
                        <Switch />
                      </Form.Item>
                      <Paragraph className="body-medium text-o-body">
                        {t("Membership")}
                      </Paragraph>
                    </Flex>
                    {formValues.is_enabled_member_benefits && (
                      <Form.Item<ecType>
                        name="member_benefits_url"
                        rules={[
                          {
                            required: formValues.is_enabled_member_benefits,
                            message: t("URL is required"),
                          },
                        ]}
                        layout="vertical"
                        className="w-full pl-[48px]"
                      >
                        <Input
                          disabled={!formValues.is_enabled_member_benefits}
                          placeholder={t("Enter URL")}
                        />
                      </Form.Item>
                    )}
                  </Flex>

                  {/* FAQ Toggle */}
                  <Flex vertical gap={"8px"}>
                    <Flex gap="12px" className="items-center">
                      <Form.Item<ecType> name="is_enabled_faq" layout="vertical">
                        <Switch />
                      </Form.Item>
                      <Paragraph className="body-medium text-o-body">
                        {t("FAQ")}
                      </Paragraph>
                    </Flex>
                    {formValues.is_enabled_faq && (
                      <Form.Item<ecType>
                        name="faq_url"
                        rules={[
                          {
                            required: formValues.is_enabled_faq,
                            message: t("URL is required"),
                          },
                        ]}
                        layout="vertical"
                        className="w-full pl-[48px]"
                      >
                        <Input
                          disabled={!formValues.is_enabled_faq}
                          placeholder={t("Enter URL")}
                        />
                      </Form.Item>
                    )}
                  </Flex>

                  {/* Order Display Toggle */}
                  <Flex vertical gap={"8px"}>
                    <Flex gap="12px" className="items-center">
                      <Form.Item<ecType>
                        name="is_enabled_order"
                        layout="vertical"
                      >
                        <Switch />
                      </Form.Item>
                      <Paragraph className="body-medium text-o-body">
                        {t("order_toggle")}
                      </Paragraph>
                    </Flex>
                  </Flex>

                  {/* Show Orders After Date Toggle */}
                  <Flex vertical gap={"8px"}>
                    <Flex gap="12px" className="items-center">
                      <Form.Item<ecType>
                        name="is_enabled_show_order_after_date"
                        layout="vertical"
                      >
                        <Switch />
                      </Form.Item>
                      <Paragraph className="body-medium text-o-body">
                        {t("order_after_date_toggle")}
                      </Paragraph>
                    </Flex>
                    {formValues.is_enabled_show_order_after_date && (
                      <Form.Item<ecType>
                        name="show_order_after_date"
                        rules={[
                          {
                            required: formValues.is_enabled_show_order_after_date,
                            message: t("Date is required"),
                          },
                        ]}
                        layout="vertical"
                        className="w-full pl-[48px]"
                      >
                        <DatePicker
                          disabled={!formValues.is_enabled_show_order_after_date}
                          placeholder={t("Select date")}
                          format="YYYY-MM-DD"
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
            <Flex vertical gap={"24px"} style={activeTab === "messaging" ? undefined : { display: "none" }}>
              <Form.Item<ecType>
                label={t("SMS Brand Name")}
                layout="vertical"
                style={{ fontSize: "24px" }}
              >
                <Text className="note-regular text-o-description">
                  {t(
                    "The brand name shown in the member binding OTP SMS. Up to 30 characters."
                  )}
                </Text>
                <Form.Item
                  name="sms_brand_name"
                  style={{ marginTop: "10px" }}
                  rules={[
                    { max: 30, message: t("Must be within 30 characters.") },
                  ]}
                >
                  <Input placeholder={t("Please enter SMS Brand Name")} />
                </Form.Item>
              </Form.Item>
            </Flex>
          </Flex>
        </Flex>
      </Form>
    );
  }
);
export default ECForm;
